/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { PopupBaseComponent } from 'src/app/common/popup/popup-base.component';
import { clipboardManager } from 'src/app/plain-javascript/services/ClipboardManager';
import { dateCache } from '@touchplan/common';
import * as strings from '@touchplan/strings';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { PopupOutputType } from 'src/app/common/popup/interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatePositionValidator } from '@appLegacy/services/date-position-validator.factory';

/**
 * A component for a popup which displays a message about clipboard tickets that
 * (partly or fully) fall beyond the Active line, and which also includes an
 * embedded calendar that lets the user optionally move the Active line to make
 * room for all of the tickets.
 */
@Component({
	selector: 'move-active-line-popup',
	templateUrl: './move-active-line-popup.component.html',
	styleUrls: ['./move-active-line-popup.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoveActiveLinePopupComponent extends PopupBaseComponent implements OnInit, OnDestroy {
	// The currently selected date in the popup's calendar
	private _selectedDate: Date | undefined;

	// This provides access to extracted string constants in the HTML template
	public appStrings = strings;

	// Calendar template properties
	public calendarNonWorkingDays: Date[] = [];
	public calendarFormControl = new UntypedFormControl('');

	public shutdownSubject$: Subject<boolean> = new Subject();

	public ngOnInit() {
		const defaultDate = dateCache.getDate(this.getMaxTicketDateInClipboard());
		this._selectedDate = defaultDate;
		this.calendarFormControl.setValue(defaultDate);
		this.calendarFormControl.valueChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe((value) => {
			this._selectedDate = value;
		});
	}

	private getMaxTicketDateInClipboard() {
		return clipboardManager.maxClipboardExactCopyWarningTicketsDate();
	}

	public formatMaxTicketDateInClipboard() {
		return dateCache.getFormat(this.getMaxTicketDateInClipboard(), 'MMM D, YYYY');
	}

	get yearRange(): string {
		return DatePositionValidator.getYearRange(5, 5);
	}

	// Overriding superclass version, in order to pass the date back via the data field
	public override writeOutput(data?) {
		this._output$.next({
			type: PopupOutputType.update,
			data: this._selectedDate,
		});
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next(true);
		this.shutdownSubject$.complete();
	}
}
